import React from "react";
import Round from "../badge/Round";

export interface DashboardSuggestionProps {
  /** Descriptive text shown above the percentage */
  description?: string;
  /** Percentage value with label (e.g., "사용자의 1%") */
  percentage?: string;
  /** Bold part of percentage text (e.g., "사용자의 1% ") */
  percentageBold?: string;
  /** Normal part of percentage text (e.g., "에요") */
  percentageNormal?: string;
  /** Card inner text */
  cardText?: string;
  /** Array of keyword badges to display */
  keywords?: string[];
  /** Optional additional CSS classes */
  className?: string;
}

/**
 * DashboardSuggestion - Displays user statistics and related keyword suggestions
 *
 * This component shows a percentage statistic comparing users with similar keywords
 * and suggests additional keywords that similar users are interested in.
 *
 * Based on Figma: dashboard/suggestion (node-id: 560-4403)
 *
 * @example
 * ```tsx
 * <DashboardSuggestion
 *   description="비슷한 관심키워드를 가진 사람들은"
 *   percentageBold="사용자의 1% "
 *   percentageNormal="에요"
 *   cardText="사람들은 이런 키워드에도 관심을 가졌어요!"
 *   keywords={["사람", "과학", "과학동아"]}
 * />
 * ```
 */
const DashboardSuggestion: React.FC<DashboardSuggestionProps> = ({
  description = "비슷한 관심키워드를 가진 사람들은",
  percentageBold = "사용자의 1% ",
  percentageNormal = "에요",
  cardText = "사람들은 이런 키워드에도 관심을을 가졌어요!",
  keywords = ["사람", "과학", "과학동아"],
  className = "",
}) => {
  return (
    <div
      className={`flex flex-col gap-2 items-start min-w-[328px] w-full ${className}`}
      data-name="dashboard/suggestion"
    >
      {/* Header with description and percentage */}
      <div className="flex gap-3 items-center w-full">
        {/* Description text */}
        <div className="flex flex-col justify-center text-[#888888] text-[12px] leading-[16px] tracking-[-0.24px] font-medium whitespace-nowrap">
          {description}
        </div>

        {/* Percentage text */}
        <div className="flex-1 flex flex-col justify-center text-[#6d2ef2] text-[16px] leading-[24px] tracking-[-0.32px] text-right">
          <p>
            <span className="font-extrabold">{percentageBold}</span>
            <span className="font-medium">{percentageNormal}</span>
          </p>
        </div>
      </div>

      {/* Card with keywords */}
      <div
        className="
          box-border
          w-full
          flex-1
          border border-solid border-[#e2d5fc]
          rounded-[20px]
          p-4
          flex flex-wrap content-center
          gap-x-5 gap-y-2
          items-center
          justify-center
        "
        data-name="card"
      >
        {/* Card text */}
        <div className="box-border text-[#212121] text-[12px] leading-[16px] tracking-[-0.24px] font-medium text-center whitespace-nowrap">
          {cardText}
        </div>

        {/* Keyword badges */}
        <div
          className="box-border flex flex-wrap gap-2 items-center justify-center"
          data-name="badge/keyword"
        >
          {keywords.map((keyword, index) => (
            <Round
              key={index}
              size="lg"
              type="secondary"
              style="fill"
              color="main"
            >
              {keyword}
            </Round>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardSuggestion;
